import { ACTIVE_BRAND } from './brand-registry.ts';
import { ENVIRONMENTS, WRANGLER_ENV_KEYS } from './config-environments.ts';
import { IFEDERATED_APP_URLS } from './ifederated-identity.ts';
import { registeredAppOrigins } from './registered-app-hosts.ts';
import { workersDevHost } from './workers-dev-origin.ts';

export const DEV_SERVER_PORT = 8787;

/** Host the local `wrangler dev` server is reached on. `wrangler dev` binds its default ip `localhost`, and every
 * consumer composes on `LOCAL_URL` below -- so the check-port bind/liveness probes MUST use this same host, never a
 * hand-spelled `127.0.0.1` (which can diverge from what wrangler actually bound). */
export const DEV_SERVER_HOST = 'localhost';

/** Worker hostnames for the storage-gateway deployments (no protocol), on the account's workers.dev subdomain
 *  (`workersDevHost`, the one spelling of it). The four remote URL members below compose protocol + host. */
const STORAGE_GATEWAY_WORKER_HOST_DEV = workersDevHost('storage-gateway-dev');
const STORAGE_GATEWAY_WORKER_HOST_PROD = workersDevHost('storage-gateway-prod');

/** Storage-gateway origins, keyed by deployment and protocol. `LOCAL_URL` is the dev-server origin composed from
 *  `DEV_SERVER_PORT`; the other four compose protocol + the worker hosts above. */
export const STORAGE_GATEWAY = {
	LOCAL_URL: `http://${DEV_SERVER_HOST}:${DEV_SERVER_PORT}`,
	DEV_URL: `https://${STORAGE_GATEWAY_WORKER_HOST_DEV}`,
	PROD_URL: `https://${STORAGE_GATEWAY_WORKER_HOST_PROD}`,
	DEV_WS_URL: `wss://${STORAGE_GATEWAY_WORKER_HOST_DEV}`,
	PROD_WS_URL: `wss://${STORAGE_GATEWAY_WORKER_HOST_PROD}`,
} as const;

/** Worker hostnames for the chats-service deployments (no protocol). The ChatRoom DO + the absorbed conversations API
 *  live here; the browser opens the ChatRoom WS directly against this host (the DO self-authenticates the upgrade). */
const CHATS_SERVICE_WORKER_HOST_DEV = workersDevHost('chats-service-dev');
const CHATS_SERVICE_WORKER_HOST_PROD = workersDevHost('chats-service-prod');

/** Chats-service origins, keyed by deployment and protocol, composed from the worker hosts above. */
export const CHATS_SERVICE = {
	DEV_URL: `https://${CHATS_SERVICE_WORKER_HOST_DEV}`,
	PROD_URL: `https://${CHATS_SERVICE_WORKER_HOST_PROD}`,
	DEV_WS_URL: `wss://${CHATS_SERVICE_WORKER_HOST_DEV}`,
	PROD_WS_URL: `wss://${CHATS_SERVICE_WORKER_HOST_PROD}`,
} as const;

export const R2_SQL_API_BASE = 'https://api.sql.cloudflarestorage.com/api/v1/accounts';

export const TWILIO_API_BASE = 'https://api.twilio.com';

export const TWILIO_ACCOUNTS_PATH = '/2010-04-01/Accounts';

export const AI_GATEWAY_BASE_URL = 'https://gateway.ai.cloudflare.com';

export const FIREBASE_TOKEN_BASE_URL = 'https://securetoken.google.com';

export const STRIPE_API_BASE = 'https://api.stripe.com';

/** Google Maps API base URL (no trailing slash). Path suffixes such as `/geocode/json`, `/place/autocomplete/json`,
 * `/staticmap`, `/streetview` are concatenated by consumers. */
export const GOOGLE_MAPS_API_BASE = 'https://maps.googleapis.com/maps/api';

/** Geocode endpoint path (relative to GOOGLE_MAPS_API_BASE). */
const GOOGLE_GEOCODE_PATH = '/geocode/json';

/**
 * The Geocoding endpoint, composed once. Two INDEPENDENT geocoders call it -- the `places-geocode` flow's fetch core
 * (`tools/places/geocode.ts`) and the property/market-maker/title-search geocoder
 * (`tools/property/google-address-handler.ts`) -- and each used to re-join base and path into its own module-local
 * name under its own spelling, which is one endpoint decision spelled twice.
 * Composed here for the same reason `CLOUDFLARE_API_V4_URL` is: a host or path move carries every caller with it.
 */
export const GOOGLE_GEOCODE_URL = `${GOOGLE_MAPS_API_BASE}${GOOGLE_GEOCODE_PATH}`;

/** Place autocomplete endpoint path (relative to GOOGLE_MAPS_API_BASE). */
export const GOOGLE_PLACES_AUTOCOMPLETE_PATH = '/place/autocomplete/json';

export const CLOUDFLARE_API_BASE_URL = 'https://api.cloudflare.com';

/** The versioned REST root every Cloudflare client-API route hangs off (`/client/v4/...`). Composed on `CLOUDFLARE_API_BASE_URL` so a host change moves every route with it; every account-scoped route composes on `CLOUDFLARE_ACCOUNTS_API_URL` below. */
export const CLOUDFLARE_API_V4_URL = `${CLOUDFLARE_API_BASE_URL}/client/v4`;

/** Cloudflare GraphQL Analytics API endpoint -- the ONE surface behind every dashboard metric panel, and a single POST route (no per-dataset paths). */
export const CLOUDFLARE_GRAPHQL_API_URL = `${CLOUDFLARE_API_V4_URL}/graphql`;

/** Root of every ACCOUNT-scoped REST route (`/accounts/<account_id>/...`); the Workers Observability query client appends the account tag and its own path. */
export const CLOUDFLARE_ACCOUNTS_API_URL = `${CLOUDFLARE_API_V4_URL}/accounts`;

/** Cloudflare Images delivery base URL (account hash for imagedelivery.net) */
const CLOUDFLARE_IMAGES_ACCOUNT_HASH = 'cJrA6ciwMSDWJEDG3_21Xg';

export const CLOUDFLARE_IMAGES_DELIVERY_BASE = `https://imagedelivery.net/${CLOUDFLARE_IMAGES_ACCOUNT_HASH}`;

/** The named variant every stored property image URL ends with: the original, up to 4096px. */
export const CLOUDFLARE_IMAGES_PUBLIC_VARIANT = 'public';

/**
 * Delivery widths for the surfaces that show a property image SMALL, addressed through a flexible variant
 * (`withDeliveryWidth`, `utils/images/delivery-variant.ts`; flexible variants were enabled on the account 2026-09-17).
 * A grid card is 293px wide and was receiving the `public` original -- 640-1495px, 74-238KB a card, about 3MB per
 * explore paint -- for a slot that needs 600px at a 2x display. The mobile feed card fills the viewport width.
 *
 * HERO is the property page's desktop main image, 60vw of the viewport: about 830px on a laptop, 1600 at 2x. Its
 * thumbnail strip (five 12vw slots) and the trade panel's 48px mini-header thumb take the CARD width; both were
 * fetching 900px originals (Lighthouse image-delivery, 499KB of savings on the property page, dev 2026-09-17).
 */
export const PROPERTY_IMAGE_DELIVERY_WIDTH = {
	CARD: 600,
	FEED: 1000,
	HERO: 1600,
} as const;

/** Default image for feed listings with no cover photo */
export const FEED_DEFAULT_IMAGE_ID = '06fe1bd9-385f-4a7c-4ea7-e40c3cbfca00';

/** Default auction bid price when no offers exist */
export const FEED_DEFAULT_BID_PRICE = 5000;

/** Synthetic base URL used when calling a Cloudflare service binding's `fetch`. The hostname is arbitrary --
 * the binding routes on the path, not the host -- but a valid absolute URL is required to satisfy the WHATWG
 * URL parser. Concatenate with `/user/<id>` + a `USER_DO_ENDPOINTS` value. */
export const INTERNAL_DO_BASE_URL = 'http://internal';

/** Synthetic base URL for the FlowRunner service binding. The DO/service binding routes on path, not host; compose as
 *  `${FLOW_INTERNAL_BASE_URL}/run/${flowName}`. Canonical producer: `callFlowBinding`. */
export const FLOW_INTERNAL_BASE_URL = 'https://flow.internal';

/** Synthetic URLs for ChatRoom DO broadcast endpoints. `CHILD` routes the per-conversation broadcast; `PARENT` fans
 * the broadcast up to a parent conversation when one exists. The pair always co-occurs at callsites. */
export const BROADCAST_URLS = {
	CHILD: 'https://internal/broadcast',
	PARENT: 'https://parent-internal/broadcast',
} as const;

// ConnectCallDO's endpoints are NOT spelled here. Every path it answers is a `VOICE_ROUTES` member
// (`const/connect-voice.ts`) -- the same record the DO's own router matches inbound paths against -- and the stub URL
// is composed by `buildDoInternalUrl`, like `buildVoiceObservePath` and `buildVoiceControlPath`, "so the dialler and the
// router cannot disagree about a path". A literal chunk URL here would be the one dialled path NOT derived that way,
// and renaming `VOICE_ROUTES.CHUNK` would then 404 every audio chunk into silence with nothing anywhere reporting it.

export const FEDERA_PROD_URL = `https://${ACTIVE_BRAND.domains.primary}` as const;

export const FEDERA_DEV_URL = `https://${ACTIVE_BRAND.domains.devPrimary}` as const;

/**
 * Local dev-server origins. NOT members of {@link ALLOWED_ORIGINS} -- they are admitted only by
 * {@link resolveAllowedOrigins} on a worker whose `environment` binding says development.
 *
 * As unconditional members of a credentialed allowlist they would be a live hole rather than an untidiness:
 * `buildCorsHeaders` defaults `allowCredentials: true`, so a PRODUCTION worker would echo
 * `Access-Control-Allow-Origin: http://localhost:3000` together with `Allow-Credentials: true` for any page served from
 * a listener on the victim's own loopback -- and a browser then permits credentialed cross-origin reads of every
 * user-scoped route from a surface no operator controls.
 */
export const LOCALHOST_DEV_ORIGINS = [
	'http://localhost:3000',
	'http://localhost:3001',
	'http://localhost:3002',
	'http://localhost:3003',
	'http://localhost:3004',
	'http://localhost:3005',
] as const;

/**
 * The CREDENTIALED-CORS allowlist, PROD-SHAPED and therefore fail-closed: a consumer that cannot state its environment
 * gets the narrow set, never the wide one. Widening happens only through {@link resolveAllowedOrigins}.
 *
 * EVERY APP OF OURS, NOT ONE BRAND'S (2026-09-21, the single sign-on hub taking every app's login): the brand's own
 * list, plus the registered app hosts of both environments (`registeredAppOrigins`, the same leaf the hub's sign-in
 * wall reads -- Kenecting and the brand's service hosts included), plus the hub's own two origins. Every app's page
 * now dials the hub's login door cross-origin, so an origin missing here is an app whose visitors cannot sign in.
 * A Set, so a host the brand list and the registered list both spell is one member.
 */
export const ALLOWED_ORIGINS: readonly string[] = [
	...new Set([
		...ACTIVE_BRAND.allowedOrigins,
		...registeredAppOrigins(WRANGLER_ENV_KEYS.DEV),
		...registeredAppOrigins(WRANGLER_ENV_KEYS.PRODUCTION),
		IFEDERATED_APP_URLS.dev,
		IFEDERATED_APP_URLS.production,
	]),
];

/** {@link ALLOWED_ORIGINS} plus the loopback pair -- the DEVELOPMENT-only allowlist. */
export const DEV_ALLOWED_ORIGINS = [...ALLOWED_ORIGINS, ...LOCALHOST_DEV_ORIGINS] as const;

/**
 * Resolve the origin allowlist for one worker env: the loopback pair joins ONLY when the env's `environment` binding
 * says development, and every other value -- production, an unset binding, a null env -- yields the narrow set.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): the comparison is `isWorkerDev`'s
 * (`utils/config/env-accessors.ts`), and the `environment` VALUE it tests is the same `ENVIRONMENTS.DEVELOPMENT`
 * member, so the two cannot disagree about what "development" is. It is HOMED HERE rather than beside `isWorkerDev`
 * for the reason `clampOffset` above it moved to `const/`: the sole consumer is `buildCorsHeaders`
 * (`utils/http/cors.ts`), and a `utils/` leaf may not value-import a `utils/` sibling under
 * `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` -- so a `utils/` home would leave the gate unreachable from the one place
 * that decides an Allow-Origin, exactly as that ban previously forced four hand-rolled pagination clamps.
 */
export function resolveAllowedOrigins(env: { environment?: string } | Record<string, unknown> | null | undefined): readonly string[] {
	return env?.environment === ENVIRONMENTS.DEVELOPMENT ? DEV_ALLOWED_ORIGINS : ALLOWED_ORIGINS;
}
